import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/ImageUpload";
import {
  LayoutDashboard, CalendarCheck, Mail, Star, FileText, Wrench, Clock,
  Images, LogOut, Trash2, Plus, Save, Loader2, Phone, ExternalLink, Building2,
  ThumbsUp, Inbox, TrendingUp, ArrowRight, Package, CheckCircle2,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Übersicht", icon: LayoutDashboard },
  { id: "appointments", label: "Terminanfragen", icon: CalendarCheck },
  { id: "contacts", label: "Kontaktanfragen", icon: Mail },
  { id: "reviews", label: "Bewertungen", icon: Star },
  { id: "content", label: "Texte & Bilder", icon: FileText },
  { id: "whyus", label: "Warum wir?", icon: ThumbsUp },
  { id: "packages", label: "Innenreinigung-Pakete", icon: Package },
  { id: "imprint", label: "Impressum", icon: Building2 },
  { id: "services", label: "Leistungen", icon: Wrench },
  { id: "hours", label: "Öffnungszeiten", icon: Clock },
  { id: "gallery", label: "Galerie", icon: Images },
];

const STATUS = ["neu", "in Bearbeitung", "bestätigt", "erledigt"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [content, setContent] = useState(null);

  const loadAll = async () => {
    try {
      const [a, c, r, ct] = await Promise.all([
        api.get("/appointments"), api.get("/contacts"),
        api.get("/admin/reviews"), api.get("/content"),
      ]);
      setAppointments(a.data); setContacts(c.data); setReviews(r.data); setContent(ct.data);
    } catch (e) { toast.error("Fehler beim Laden der Daten."); }
  };
  useEffect(() => { loadAll(); }, []);

  const doLogout = async () => { await logout(); navigate("/admin"); };

  // ----- appointments / contacts -----
  const updateStatus = async (type, id, status) => {
    await api.patch(`/${type}/${id}`, { status });
    toast.success(status === "bestätigt" ? "Bestätigt – Kunde wurde per E-Mail benachrichtigt." : "Status aktualisiert.");
    type === "appointments" ? loadAppts() : loadContacts();
  };
  const loadAppts = async () => setAppointments((await api.get("/appointments")).data);
  const loadContacts = async () => setContacts((await api.get("/contacts")).data);
  const delItem = async (type, id) => {
    await api.delete(`/${type}/${id}`);
    toast.success("Gelöscht.");
    type === "appointments" ? loadAppts() : loadContacts();
  };

  // ----- reviews -----
  const newReview = { author: "", rating: 5, text: "", source: "Google", published: true };
  const [reviewForm, setReviewForm] = useState(newReview);
  const addReview = async () => {
    if (!reviewForm.author || !reviewForm.text) return toast.error("Bitte Autor und Text angeben.");
    await api.post("/reviews", reviewForm);
    toast.success("Bewertung hinzugefügt.");
    setReviewForm(newReview);
    setReviews((await api.get("/admin/reviews")).data);
  };
  const delReview = async (id) => {
    await api.delete(`/reviews/${id}`);
    setReviews((await api.get("/admin/reviews")).data);
    toast.success("Bewertung gelöscht.");
  };
  const togglePublish = async (rv) => {
    await api.put(`/reviews/${rv.id}`, { ...rv, published: !rv.published });
    setReviews((await api.get("/admin/reviews")).data);
  };

  // ----- content -----
  const [saving, setSaving] = useState(false);
  const saveContent = async () => {
    setSaving(true);
    try {
      const { id, ...data } = content;
      await api.put("/content", { data });
      toast.success("Änderungen gespeichert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };
  const setC = (k, v) => setContent({ ...content, [k]: v });

  const newAppts = appointments.filter((a) => a.status === "neu").length;
  const newContacts = contacts.filter((c) => c.status === "neu").length;
  const badges = { appointments: newAppts, contacts: newContacts };

  if (!content) return <div className="min-h-screen flex items-center justify-center bg-[#09090b]"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-zinc-950 border-r border-white/5 hidden md:flex flex-col fixed h-full">
        <div className="p-6 border-b border-white/5">
          <div className="font-heading font-extrabold text-lg text-white">BERISHA</div>
          <div className="text-red-500 text-[10px] tracking-[0.3em] uppercase">Admin Portal</div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-all ${tab === t.id ? "bg-red-600 text-white shadow-lg shadow-red-900/30" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"}`}>
              <t.icon className="w-4 h-4 shrink-0" /> <span className="flex-1 text-left">{t.label}</span>
              {badges[t.id] > 0 && (
                <span className={`text-[10px] font-bold min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center ${tab === t.id ? "bg-white text-red-600" : "bg-red-600 text-white"}`}>{badges[t.id]}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5">
          <button data-testid="logout-btn" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white">
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 p-5 sm:p-8">
        {/* mobile tab select */}
        <div className="md:hidden mb-5 flex items-center justify-between">
          <select value={tab} onChange={(e) => setTab(e.target.value)} className="input-dark !w-auto" data-testid="mobile-tab-select">
            {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <button onClick={doLogout} className="btn-secondary !py-2 !px-4 text-sm"><LogOut className="w-4 h-4" /></button>
        </div>

        <header className="hidden md:flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><LayoutDashboard className="w-6 h-6 text-red-500" /> {TABS.find(t => t.id === tab)?.label}</h1>
            <p className="text-zinc-500 text-sm">Angemeldet als {user?.email}</p>
          </div>
          <a href="/" target="_blank" rel="noreferrer" className="btn-secondary !py-2.5 text-sm"><ExternalLink className="w-4 h-4" /> Website ansehen</a>
        </header>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div data-testid="overview-panel" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={CalendarCheck} label="Terminanfragen" value={appointments.length} sub={`${newAppts} neu`} onClick={() => setTab("appointments")} />
              <StatCard icon={Mail} label="Kontaktanfragen" value={contacts.length} sub={`${newContacts} neu`} onClick={() => setTab("contacts")} />
              <StatCard icon={Star} label="Bewertungen" value={reviews.length} sub={`${reviews.filter(r => r.published).length} sichtbar`} onClick={() => setTab("reviews")} />
              <StatCard icon={Wrench} label="Leistungen" value={(content.services || []).length} sub="aktiv" onClick={() => setTab("services")} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card-dark p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2"><Inbox className="w-4 h-4 text-red-500" /> Neueste Terminanfragen</h3>
                  <button onClick={() => setTab("appointments")} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">Alle <ArrowRight className="w-3 h-3" /></button>
                </div>
                <div className="space-y-2">
                  {appointments.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                      <div><span className="text-white">{a.name}</span> <span className="text-zinc-500">· {a.service || "—"}</span></div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.status === "neu" ? "bg-red-600/20 text-red-400" : "bg-zinc-800 text-zinc-400"}`}>{a.status}</span>
                    </div>
                  ))}
                  {appointments.length === 0 && <p className="text-zinc-600 text-sm">Noch keine Anfragen.</p>}
                </div>
              </div>
              <div className="card-dark p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-red-500" /> Neueste Kontaktanfragen</h3>
                  <button onClick={() => setTab("contacts")} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">Alle <ArrowRight className="w-3 h-3" /></button>
                </div>
                <div className="space-y-2">
                  {contacts.slice(0, 5).map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                      <div><span className="text-white">{c.name}</span> <span className="text-zinc-500">· {c.phone}</span></div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "neu" ? "bg-red-600/20 text-red-400" : "bg-zinc-800 text-zinc-400"}`}>{c.status}</span>
                    </div>
                  ))}
                  {contacts.length === 0 && <p className="text-zinc-600 text-sm">Noch keine Anfragen.</p>}
                </div>
              </div>
            </div>

            <div className="card-dark p-6">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-red-500" /> Schnellzugriff</h3>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setTab("content")} className="btn-secondary !py-2.5 text-sm"><FileText className="w-4 h-4" /> Texte & Bilder</button>
                <button onClick={() => setTab("services")} className="btn-secondary !py-2.5 text-sm"><Wrench className="w-4 h-4" /> Leistungen</button>
                <button onClick={() => setTab("hours")} className="btn-secondary !py-2.5 text-sm"><Clock className="w-4 h-4" /> Öffnungszeiten</button>
                <button onClick={() => setTab("gallery")} className="btn-secondary !py-2.5 text-sm"><Images className="w-4 h-4" /> Galerie</button>
                <a href="/" target="_blank" rel="noreferrer" className="btn-primary !py-2.5 text-sm"><ExternalLink className="w-4 h-4" /> Website ansehen</a>
              </div>
            </div>
          </div>
        )}

        {/* APPOINTMENTS */}
        {tab === "appointments" && (
          <div data-testid="appointments-panel" className="space-y-4">
            {appointments.length === 0 && <Empty text="Noch keine Terminanfragen." />}
            {appointments.map((a) => (
              <div key={a.id} className="card-dark p-5">
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div>
                    <div className="text-white font-semibold">{a.name} <span className="text-zinc-500 font-normal text-sm">· {a.phone}</span></div>
                    <div className="text-sm text-zinc-400 mt-1">{[a.vehicle_brand, a.vehicle_model].filter(Boolean).join(" ")} {a.service && `· ${a.service}`}</div>
                    {a.preferred_date && <div className="text-sm text-zinc-400">Wunschtermin: {a.preferred_date}</div>}
                    {a.email && <div className="text-sm text-zinc-500">{a.email}</div>}
                    {a.message && <p className="text-sm text-zinc-300 mt-2 bg-zinc-950 rounded-xl p-3">{a.message}</p>}
                    <div className="text-xs text-zinc-600 mt-2">{new Date(a.created_at).toLocaleString("de-DE")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={a.status} onChange={(e) => updateStatus("appointments", a.id, e.target.value)} className="input-dark !w-auto !py-2 text-sm">
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button data-testid={`confirm-appointment-${a.id}`} onClick={() => updateStatus("appointments", a.id, "bestätigt")} title="Bestätigen & Kunde per E-Mail benachrichtigen" className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-green-600/20 text-green-400 hover:bg-green-600 hover:text-white text-sm"><CheckCircle2 className="w-4 h-4" /> Bestätigen</button>
                    <a href={`tel:${a.phone}`} className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-blue-600 hover:text-white"><Phone className="w-4 h-4" /></a>
                    <button onClick={() => delItem("appointments", a.id)} className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-red-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && (
          <div data-testid="contacts-panel" className="space-y-4">
            {contacts.length === 0 && <Empty text="Noch keine Kontaktanfragen." />}
            {contacts.map((c) => (
              <div key={c.id} className="card-dark p-5">
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div>
                    <div className="text-white font-semibold">{c.name} <span className="text-zinc-500 font-normal text-sm">· {c.phone}</span></div>
                    {c.vehicle && <div className="text-sm text-zinc-400 mt-1">Fahrzeug: {c.vehicle}</div>}
                    {c.email && <div className="text-sm text-zinc-500">{c.email}</div>}
                    <p className="text-sm text-zinc-300 mt-2 bg-zinc-950 rounded-xl p-3">{c.message}</p>
                    <div className="text-xs text-zinc-600 mt-2">{new Date(c.created_at).toLocaleString("de-DE")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={c.status} onChange={(e) => updateStatus("contacts", c.id, e.target.value)} className="input-dark !w-auto !py-2 text-sm">
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button data-testid={`confirm-contact-${c.id}`} onClick={() => updateStatus("contacts", c.id, "bestätigt")} title="Bestätigen & Kunde per E-Mail benachrichtigen" className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-green-600/20 text-green-400 hover:bg-green-600 hover:text-white text-sm"><CheckCircle2 className="w-4 h-4" /> Bestätigen</button>
                    <button onClick={() => delItem("contacts", c.id)} className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-red-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* REVIEWS */}
        {tab === "reviews" && (
          <div data-testid="reviews-panel" className="space-y-5">
            <div className="card-dark p-6 space-y-3">
              <h3 className="text-white font-semibold">Neue Bewertung hinzufügen</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input className="input-dark" placeholder="Autor" value={reviewForm.author} onChange={(e) => setReviewForm({ ...reviewForm, author: e.target.value })} data-testid="review-author" />
                <input className="input-dark" placeholder="Quelle" value={reviewForm.source} onChange={(e) => setReviewForm({ ...reviewForm, source: e.target.value })} />
                <select className="input-dark" value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })}>
                  {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} Sterne</option>)}
                </select>
              </div>
              <textarea className="input-dark resize-none" rows="2" placeholder="Bewertungstext" value={reviewForm.text} onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })} data-testid="review-text" />
              <button onClick={addReview} className="btn-primary text-sm" data-testid="review-add"><Plus className="w-4 h-4" /> Hinzufügen</button>
            </div>
            {reviews.map((r) => (
              <div key={r.id} className="card-dark p-5 flex justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2"><span className="text-white font-semibold">{r.author}</span><div className="flex">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-red-500 text-red-500" />)}</div></div>
                  <p className="text-sm text-zinc-400 mt-1">{r.text}</p>
                </div>
                <div className="flex items-start gap-2 shrink-0">
                  <button onClick={() => togglePublish(r)} className={`text-xs px-3 py-1.5 rounded-full ${r.published ? "bg-green-600/20 text-green-400" : "bg-zinc-800 text-zinc-400"}`}>{r.published ? "Sichtbar" : "Versteckt"}</button>
                  <button onClick={() => delReview(r.id)} className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-red-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CONTENT */}
        {tab === "content" && (
          <div data-testid="content-panel" className="space-y-5 max-w-3xl">
            <Section title="Hero-Bereich">
              <Field label="Überschrift"><textarea className="input-dark resize-none" rows="2" value={content.hero_title} onChange={(e) => setC("hero_title", e.target.value)} data-testid="edit-hero-title" /></Field>
              <Field label="Unterüberschrift"><textarea className="input-dark resize-none" rows="2" value={content.hero_subtitle} onChange={(e) => setC("hero_subtitle", e.target.value)} /></Field>
              <ImageUpload label="Hero-Bild" testid="upload-hero" value={content.hero_image} onChange={(v) => setC("hero_image", v)} />
            </Section>
            <Section title="Über uns">
              <Field label="Titel"><input className="input-dark" value={content.about_title} onChange={(e) => setC("about_title", e.target.value)} /></Field>
              <Field label="Text"><textarea className="input-dark resize-none" rows="4" value={content.about_text} onChange={(e) => setC("about_text", e.target.value)} /></Field>
              <ImageUpload label="Über-uns Bild" testid="upload-about" value={content.about_image} onChange={(v) => setC("about_image", v)} />
            </Section>
            <Section title="Kontaktdaten">
              <Field label="Telefon"><input className="input-dark" value={content.phone} onChange={(e) => setC("phone", e.target.value)} /></Field>
              <Field label="E-Mail"><input className="input-dark" value={content.email} onChange={(e) => setC("email", e.target.value)} data-testid="edit-email" /></Field>
              <Field label="Adresse"><input className="input-dark" value={content.address} onChange={(e) => setC("address", e.target.value)} /></Field>
              <Field label="Instagram URL"><input className="input-dark" value={content.instagram} onChange={(e) => setC("instagram", e.target.value)} /></Field>
              <Field label="Instagram Handle"><input className="input-dark" value={content.instagram_handle} onChange={(e) => setC("instagram_handle", e.target.value)} /></Field>
            </Section>
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}

        {/* IMPRINT */}
        {tab === "imprint" && (
          <div data-testid="imprint-panel" className="space-y-5 max-w-3xl">
            <Section title="Impressum-Angaben">
              <Field label="Firmenname"><input className="input-dark" value={content.imprint_company || ""} onChange={(e) => setC("imprint_company", e.target.value)} /></Field>
              <Field label="Inhaber / Vertreten durch"><input className="input-dark" value={content.imprint_owner || ""} onChange={(e) => setC("imprint_owner", e.target.value)} data-testid="edit-imprint-owner" /></Field>
              <Field label="Anschrift"><input className="input-dark" value={content.imprint_address || ""} onChange={(e) => setC("imprint_address", e.target.value)} /></Field>
              <Field label="E-Mail (Impressum & Kontakt)"><input className="input-dark" value={content.email || ""} onChange={(e) => setC("email", e.target.value)} data-testid="edit-imprint-email" /></Field>
              <Field label="Telefon"><input className="input-dark" value={content.phone || ""} onChange={(e) => setC("phone", e.target.value)} /></Field>
              <Field label="Umsatzsteuer-ID (USt-IdNr.)"><input className="input-dark" value={content.imprint_ust_id || ""} onChange={(e) => setC("imprint_ust_id", e.target.value)} placeholder="z. B. DE123456789" data-testid="edit-imprint-ustid" /></Field>
            </Section>
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}

        {/* WHY US */}
        {tab === "whyus" && (
          <div data-testid="whyus-edit-panel" className="space-y-4 max-w-3xl">
            <p className="text-zinc-500 text-sm">Bearbeiten Sie die Vorteile, die im Bereich „Warum wir?" angezeigt werden.</p>
            {(content.why_us || []).map((w, i) => (
              <div key={i} className="card-dark p-4 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-start">
                <input className="input-dark" value={w.title} placeholder="Titel" onChange={(e) => { const wu = [...content.why_us]; wu[i] = { ...wu[i], title: e.target.value }; setC("why_us", wu); }} />
                <textarea className="input-dark resize-none" rows="2" value={w.desc} placeholder="Beschreibung" onChange={(e) => { const wu = [...content.why_us]; wu[i] = { ...wu[i], desc: e.target.value }; setC("why_us", wu); }} />
                <button onClick={() => setC("why_us", content.why_us.filter((_, j) => j !== i))} className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-red-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setC("why_us", [...(content.why_us || []), { title: "Neuer Vorteil", desc: "", icon: "ThumbsUp" }])} className="btn-secondary text-sm"><Plus className="w-4 h-4" /> Vorteil hinzufügen</button>
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}

        {/* PACKAGES */}
        {tab === "packages" && (
          <div data-testid="packages-edit-panel" className="space-y-4 max-w-3xl">
            <p className="text-zinc-500 text-sm">Bearbeiten Sie die Innenreinigungs-Pakete (Name, Dauer, Titel, Leistungen, Preis).</p>
            {(content.packages || []).map((p, i) => {
              const upd = (field, val) => { const pk = [...content.packages]; pk[i] = { ...pk[i], [field]: val }; setC("packages", pk); };
              return (
                <div key={i} className="card-dark p-5 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input className="input-dark" placeholder="Name (z.B. Paket 1)" value={p.name} onChange={(e) => upd("name", e.target.value)} />
                    <input className="input-dark" placeholder="Dauer (z.B. in 20 Min.)" value={p.duration} onChange={(e) => upd("duration", e.target.value)} />
                    <input className="input-dark" placeholder="Preis (z.B. ab 45 €)" value={p.price} onChange={(e) => upd("price", e.target.value)} />
                  </div>
                  <input className="input-dark" placeholder="Titel" value={p.title} onChange={(e) => upd("title", e.target.value)} />
                  <textarea className="input-dark resize-none" rows="3" placeholder="Leistungen – eine pro Zeile" value={(p.items || []).join("\n")} onChange={(e) => upd("items", e.target.value.split("\n").filter(x => x.trim() !== ""))} />
                  <label className="flex items-center gap-2 text-sm text-zinc-400">
                    <input type="checkbox" checked={!!p.featured} onChange={(e) => upd("featured", e.target.checked)} /> Als „Beliebt" hervorheben
                  </label>
                  <button onClick={() => setC("packages", content.packages.filter((_, j) => j !== i))} className="text-sm text-red-400 flex items-center gap-1"><Trash2 className="w-4 h-4" /> Paket entfernen</button>
                </div>
              );
            })}
            <button onClick={() => setC("packages", [...(content.packages || []), { name: "Neues Paket", duration: "", title: "", items: [], price: "", featured: false }])} className="btn-secondary text-sm"><Plus className="w-4 h-4" /> Paket hinzufügen</button>
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}
        {tab === "services" && (
          <div data-testid="services-edit-panel" className="space-y-4 max-w-3xl">
            {(content.services || []).map((s, i) => (
              <div key={i} className="card-dark p-4 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-start">
                <input className="input-dark" value={s.title} placeholder="Titel" onChange={(e) => { const sv = [...content.services]; sv[i] = { ...sv[i], title: e.target.value }; setC("services", sv); }} />
                <textarea className="input-dark resize-none" rows="2" value={s.desc} placeholder="Beschreibung" onChange={(e) => { const sv = [...content.services]; sv[i] = { ...sv[i], desc: e.target.value }; setC("services", sv); }} />
                <button onClick={() => setC("services", content.services.filter((_, j) => j !== i))} className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-red-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setC("services", [...(content.services || []), { title: "Neue Leistung", desc: "", icon: "Sparkles" }])} className="btn-secondary text-sm"><Plus className="w-4 h-4" /> Leistung hinzufügen</button>
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}

        {/* HOURS */}
        {tab === "hours" && (
          <div data-testid="hours-panel" className="space-y-3 max-w-xl">
            {(content.opening_hours || []).map((o, i) => (
              <div key={i} className="card-dark p-4 grid grid-cols-2 gap-3 items-center">
                <input className="input-dark" value={o.day} onChange={(e) => { const h = [...content.opening_hours]; h[i] = { ...h[i], day: e.target.value }; setC("opening_hours", h); }} />
                <input className="input-dark" value={o.hours} onChange={(e) => { const h = [...content.opening_hours]; h[i] = { ...h[i], hours: e.target.value }; setC("opening_hours", h); }} />
              </div>
            ))}
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}

        {/* GALLERY */}
        {tab === "gallery" && (
          <div data-testid="gallery-panel" className="space-y-4 max-w-3xl">
            {(content.gallery || []).map((g, i) => (
              <div key={i} className="card-dark p-4 space-y-3">
                <input className="input-dark" placeholder="Bezeichnung" value={g.label} onChange={(e) => { const gl = [...content.gallery]; gl[i] = { ...gl[i], label: e.target.value }; setC("gallery", gl); }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ImageUpload label="Vorher" testid={`upload-before-${i}`} value={g.before} onChange={(v) => { const gl = [...content.gallery]; gl[i] = { ...gl[i], before: v }; setC("gallery", gl); }} />
                  <ImageUpload label="Nachher" testid={`upload-after-${i}`} value={g.after} onChange={(v) => { const gl = [...content.gallery]; gl[i] = { ...gl[i], after: v }; setC("gallery", gl); }} />
                </div>
                <button onClick={() => setC("gallery", content.gallery.filter((_, j) => j !== i))} className="text-sm text-red-500 flex items-center gap-1"><Trash2 className="w-4 h-4" /> Entfernen</button>
              </div>
            ))}
            <button onClick={() => setC("gallery", [...(content.gallery || []), { label: "Neu", before: "", after: "" }])} className="btn-secondary text-sm"><Plus className="w-4 h-4" /> Galerie-Eintrag hinzufügen</button>
            <SaveBar onSave={saveContent} saving={saving} />
          </div>
        )}
      </main>
    </div>
  );
}

const Empty = ({ text }) => <div className="card-dark p-10 text-center text-zinc-500">{text}</div>;
const StatCard = ({ icon: Icon, label, value, sub, onClick }) => (
  <button onClick={onClick} data-testid={`stat-${label}`} className="card-dark p-5 text-left hover:-translate-y-1 group">
    <div className="flex items-center justify-between mb-3">
      <span className="w-10 h-10 rounded-xl bg-red-600/15 flex items-center justify-center group-hover:bg-red-600 transition-colors"><Icon className="w-5 h-5 text-red-500 group-hover:text-white transition-colors" /></span>
    </div>
    <div className="text-3xl font-extrabold text-white leading-none">{value}</div>
    <div className="text-sm text-zinc-400 mt-1">{label}</div>
    <div className="text-xs text-red-500 mt-1">{sub}</div>
  </button>
);
const Section = ({ title, children }) => <div className="card-dark p-6 space-y-4"><h3 className="text-white font-semibold">{title}</h3>{children}</div>;
const Field = ({ label, children }) => <div><label className="text-xs text-zinc-500 mb-1.5 block">{label}</label>{children}</div>;
const SaveBar = ({ onSave, saving }) => (
  <div className="sticky bottom-4">
    <button onClick={onSave} disabled={saving} data-testid="save-content" className="btn-primary w-full">
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Änderungen speichern</>}
    </button>
  </div>
);
